/* OfflineClockQueue — IndexedDB-backed queue for clock-in / clock-out
 * requests when the network is unavailable.
 *
 * Lifecycle:
 *   1. Page calls `enqueueClockRequest({ kind: 'in' | 'out', queued_at })`
 *      after a fetch fails (or before — see handleClockTap below).
 *   2. Each entry gets a UUID `client_request_id`. Stored in 'pending' store.
 *   3. We register a Background Sync tag 'clc-clock-queue' with the SW.
 *   4. The SW (or this same module on focus) tries to flush:
 *        POST /api/clock/{kind} { client_request_id, queued_at }
 *      with credentials. On 200, the entry is deleted. On 4xx (e.g. already
 *      clocked in), the entry is deleted because the server's truth wins.
 *      On network failure, leave it pending and try again later.
 *   5. UI subscribes to changes via the `subscribePending()` listener.
 */

const DB_NAME = "clc-portal";
const STORE = "clock-queue";
const SYNC_TAG = "clc-clock-queue";
const VERSION = 1;

let listeners = new Set();
function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      // Subscriber threw — don't let one bad listener break the rest of
      // the chain. Log for diagnostics so we can find buggy callbacks.
      // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
      if (typeof console !== "undefined") console.warn("Offline-queue subscriber threw:", err?.message || err);
    }
  });
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "client_request_id" });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

async function dbAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}
async function dbPut(row) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function dbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function uuid() {
  // Falls back to a Math.random uuid for browsers without crypto.randomUUID
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

async function registerSync() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if ("sync" in reg) {
      await reg.sync.register(SYNC_TAG);
    }
  } catch (err) {
    // Background Sync isn't supported (Safari/iOS) OR the SW hasn't taken
    // control yet — fine, we'll flush on focus / online events instead.
    // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
    if (typeof console !== "undefined") console.warn("Background Sync register skipped:", err?.message || err);
  }
}

/** Enqueue a clock-in or clock-out for replay when online. */
export async function enqueueClockRequest({ kind }) {
  if (kind !== "in" && kind !== "out") throw new Error("kind must be 'in' or 'out'");
  const row = {
    client_request_id: uuid(),
    kind,
    queued_at: new Date().toISOString(),
  };
  await dbPut(row);
  await registerSync();
  notify();
  return row;
}

export async function getPending() {
  return dbAll();
}

export function subscribePending(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Try flushing every pending entry. Returns { sent, failed }. */
export async function flushPending(apiBase) {
  const items = await dbAll();
  let sent = 0;
  let failed = 0;
  for (const item of items) {
    try {
      const res = await fetch(`${apiBase}/clock/${item.kind}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_request_id: item.client_request_id,
          queued_at: item.queued_at,
        }),
      });
      if (res.ok || (res.status >= 400 && res.status < 500)) {
        // Server's truth wins — drop the entry. (4xx = "already clocked in"
        // or similar; don't keep retrying forever.)
        await dbDelete(item.client_request_id);
        if (res.ok) sent += 1;
        else failed += 1;
      } else {
        // 5xx — leave it for the next attempt
        failed += 1;
      }
    } catch (err) {
      // Network down — leave the row pending; the next online/focus event
      // will retry it. Counted in `failed` so the caller can surface a toast.
      // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
      if (typeof console !== "undefined") console.warn("Offline-queue flush retry needed:", err?.message || err);
      failed += 1;
    }
  }
  notify();
  return { sent, failed };
}

/** Wire up automatic flushing on connectivity changes + page focus.
 *  @param apiBase string  — base URL for the /clock/* endpoints.
 *  @param onAfterFlush ?(summary: {sent:number, failed:number}) => void
 *         optional callback invoked after every flush attempt — use this
 *         to reload the dashboard status card so the UI reflects the
 *         shifts the server just wrote. */
export function startAutoFlush(apiBase, onAfterFlush) {
  const tryFlush = async () => {
    if (typeof navigator !== "undefined" && navigator.onLine) {
      try {
        const summary = await flushPending(apiBase);
        if (typeof onAfterFlush === "function") onAfterFlush(summary);
      } catch (err) {
        // Background tick errored (e.g. transient 500). Next online/focus
        // event will retry — no user-visible toast needed.
        // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
        if (typeof console !== "undefined") console.warn("Offline-queue auto-flush retry needed:", err?.message || err);
      }
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("online", tryFlush);
    window.addEventListener("focus", tryFlush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") tryFlush();
    });
    // Initial poke 2s after boot to drain anything left over.
    setTimeout(tryFlush, 2000);
  }
}
